/**
 * Mapping Service – Phone → Country Code
 *
 * Carica il file di mapping generato da be/bot/mapping.php e lo cachea
 * in memoria (e opzionalmente in localStorage) per l'intera sessione.
 *
 * Uso:
 *   await loadPhoneMapping();          // da chiamare all'avvio (App.tsx)
 *   getCountryFromPhone('+39...')       // 'it' | 'ch' — sincrono una volta caricato
 */

import { API_CONFIG } from '../config/api';

export type CountryCode = 'it' | 'ch';

// ─── Cache in memoria ─────────────────────────────────────────────────────────
let memoryMapping: Record<string, CountryCode> | null = null;
let loadPromise: Promise<void> | null = null;

// ─── Cache in localStorage ────────────────────────────────────────────────────
const LS_KEY       = 'phone_country_mapping';
const LS_TS_KEY    = 'phone_country_mapping_ts';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 ora

function getFromLocalStorage(): Record<string, CountryCode> | null {
  try {
    const ts   = Number(localStorage.getItem(LS_TS_KEY) ?? '0');
    const data = localStorage.getItem(LS_KEY);
    if (data && Date.now() - ts < CACHE_TTL_MS) {
      return JSON.parse(data) as Record<string, CountryCode>;
    }
  } catch {
    // localStorage non disponibile o dati corrotti
  }
  return null;
}

function saveToLocalStorage(mapping: Record<string, CountryCode>): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(mapping));
    localStorage.setItem(LS_TS_KEY, String(Date.now()));
  } catch {
    // localStorage pieno o non disponibile
  }
}

// ─── Caricamento del mapping ──────────────────────────────────────────────────

/**
 * Carica il mapping telefono→paese dall'endpoint remoto (o dalla cache).
 * È idempotente: chiamate multiple restituiscono la stessa Promise.
 */
export async function loadPhoneMapping(): Promise<void> {
  // Già caricato in memoria
  if (memoryMapping !== null) return;

  // Evita fetch paralleli
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    // 1. Prova localStorage
    const cached = getFromLocalStorage();
    if (cached) {
      memoryMapping = cached;
      console.log(`📋 Phone mapping caricato da localStorage (${Object.keys(cached).length} voci)`);
      return;
    }

    // 2. Fetch remoto
    try {
      const url = API_CONFIG.MAPPING_URL;
      console.log(`🌐 Caricamento phone mapping da: ${url}`);
      const response = await fetch(url, { method: 'GET' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json() as Record<string, CountryCode>;
      memoryMapping = data;
      saveToLocalStorage(data);
      console.log(`✅ Phone mapping caricato: ${Object.keys(data).length} voci`);
    } catch (err) {
      console.warn('⚠️ Impossibile caricare il phone mapping:', err);
      // Fallback: mapping vuoto (si userà il prefisso telefonico)
      memoryMapping = {};
    }
  })();

  return loadPromise;
}

/**
 * Determina il country code di un numero di telefono.
 *
 * Priorità:
 *  1. Mapping caricato da remoto (fonte più accurata)
 *  2. Prefisso telefonico (+41 / 0041 → 'ch', tutto il resto → 'it')
 *
 * @param phone - Numero di telefono (qualsiasi formato)
 * @returns 'it' | 'ch'
 */
export function getCountryFromPhone(phone: string): CountryCode {
  if (!phone) return 'it';

  const normalized = phone.trim();

  // 1. Lookup nel mapping
  if (memoryMapping !== null) {
    const mapped = memoryMapping[normalized];
    if (mapped) return mapped;

    // Prova anche senza spazi intermedi
    const compact = normalized.replace(/\s+/g, '');
    const mappedCompact = memoryMapping[compact];
    if (mappedCompact) return mappedCompact;
  }

  // 2. Fallback da prefisso
  if (normalized.startsWith('+41') || normalized.startsWith('0041')) {
    return 'ch';
  }

  return 'it';
}

/**
 * Invalida la cache (memoria + localStorage) e forza il prossimo caricamento.
 */
export function invalidateMappingCache(): void {
  memoryMapping = null;
  loadPromise   = null;
  try {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem(LS_TS_KEY);
  } catch {
    // ignore
  }
}

/**
 * Restituisce true se il mapping è già stato caricato in memoria.
 */
export function isMappingLoaded(): boolean {
  return memoryMapping !== null;
}

